import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Fuse from "fuse.js";
import { Search, RefreshCw, Download } from "react-feather";
import {
  Banner,
  Button,
  Input,
  Section,
  Spinner,
  Table,
  TableRow,
} from "@cinesys/ui/components";
import SpotterHeader from "../SpotterHeader";
import { SPOTTER_STRINGS } from "../strings";
import { SPOTTER_INTERFACE_ID } from "../config";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { useInterfaceAccess } from "../../../hooks/useInterfaceAccess";
import InterfaceAccessNotice from "../../../components/InterfaceAccessNotice";
import { DelayedSpinner } from "@cinesys/experience-ui/components";
import SeasonWeekPicker from "./SeasonWeekPicker";
import CopyButton from "./CopyButton";
import { weekLabel } from "./weeks";
import {
  GamebookCompletedGame,
  GamebookPlay,
  useGamebookConfig,
  useGamebookMatchup,
  useGamebookWeeks,
  useGenerateGamebook,
} from "./service";
import {
  flagsCellContent,
  hasAnyFlag,
  playCellContent,
  situationCellContent,
  computeScoredIndices,
} from "./gamebookCellContent";
import { ApiError } from "../../../CustomerApiService";

/**
 * Spotter Gamebook tab (follow-up to PR #210, `feat/spotter-gamebook-tab`). Built against the
 * reference mock (`/tmp/gamebook.html`, read directly — not copied into the repo; its inline
 * `DATA` blob is large sample play-by-play data, not something to vendor) using REAL shared
 * components (`Section`/`Table`/`Button`/`Input`/`Select`) rather than a literal port of the
 * mock's hand-rolled CSS, same convention `FieldsScreen.tsx` follows. Uses
 * the plain `Table` (not `ThemedTable`, which is an `apps/frontend`-only convention overriding
 * `--dark300` for the manager app's own palette) — `CandidateAssignmentsScreen.tsx` is
 * customer-frontend's own precedent for this, and Spotter's `--spotterLine`/`--spotterLine2`
 * variable aliasing below (`Wrap`) re-skins `Table`'s own tokens directly instead.
 *
 * Layout differs from the mock in the one place Addison called out explicitly: season/week
 * selection is its own section BELOW `SpotterHeader` (`SeasonWeekPicker.tsx`), not inside the
 * sticky header row the mock puts it in.
 *
 * "One row per matchup" (not a league-wide slate): a customer org maps to exactly one NFL club
 * (`nflClubCode`, server-side config), which plays at most one game per selected week — see
 * `apps/customer-api/src/routes/spotter/gamebook.ts`'s doc comment for the full backend story,
 * including what's real vs. mocked (`master_play_by_play` reads and the NFL schedule-API preview
 * are both currently mocked server-side; this screen renders whatever the API returns either way,
 * so it needs no changes once the real integrations land).
 *
 * Data fetching: every network call goes through `./service.ts`'s `react-query` hooks — see
 * `apps/customer-frontend/CLAUDE.md`'s "Data fetching (react-query)" section for why that's the
 * house convention, not a per-feature choice.
 */

/** `flex-shrink: 0`: `CustomerLayout`'s `Content` is a flex column with a real height ceiling
 *  (`Shell`'s `100vh`), and `Page` is its only child — by default a flex item shrinks to fit that
 *  ceiling even when its own content (the whole scrollable page, here well past viewport height)
 *  needs more room, so `Page` was rendering at exactly viewport height while everything past that
 *  point just painted outside its box. `ColheadSticky`'s actual containing block is `Wrap`, which
 *  isn't a flex item and always sizes to its content correctly — so this didn't visibly break the
 *  sticky mechanism in every engine, but it's still the real defect Addison traced it to: measured
 *  live (Chrome, real `GamebookScreen`, 60-play matchup), `Page`'s box stopped at 900px while the
 *  document scrolled to 3605px. `flex-shrink: 0` keeps `min-height: 100%`'s floor for a short page
 *  while letting a tall one grow past `Content`'s ceiling like every other block box already does. */
const Page = styled.div`
  min-height: 100%;
  flex-shrink: 0;
  color: var(--spotterInk);
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
`;

/** Mirrors `FieldsScreen.tsx`'s `Wrap` — single-column here (no changes panel), so no
 *  `PageWrapperTwoColumn`. */
const Wrap = styled.div`
  padding: 20px 22px 40px;
  max-width: 1320px;
  margin: 0 auto;

  --inputBackground: var(--spotterRaised);
  --inputBorder: var(--spotterLine2);
  --dark400: var(--spotterRaised);
  --dark500: var(--spotterLine2);
  --dark600: var(--spotterLine2);
  --dark700: var(--spotterInkFaint);
  --gray400: var(--spotterInk);
  --gray500: var(--spotterInk);
  --blue060: var(--spotterAccent);
  --blue100: var(--spotterAccent);
  --selectOptionHover: var(--spotterAccentHover);
  --blue400: var(--spotterAccent);
  --blue500: var(--spotterAccent);
  --blueBorder: var(--spotterAccentLine);
  --blueBorderOverlay: var(--spotterAccentLine);
  --blueBackground: var(--spotterAccentSoft);
  --blueFocusRing: var(--spotterAccentSoft);
  --green400: var(--spotterOk);
  --green500: var(--spotterOk);
  --greenBorder: var(--spotterOkLine);
  --greenBorderOverlay: var(--spotterOkLine);
  --greenBackground: var(--spotterOkSoft);
  --red400: var(--spotterDanger);
  --red500: var(--spotterDanger);
  --redBorder: var(--spotterDangerBorder);
  --redBorderOverlay: var(--spotterDangerBorder);
  --redBackground: var(--spotterDangerSoft);
`;

const SelectPortalHost = styled.div`
  display: contents;
`;

const StatusMessage = styled.div`
  padding: 16px 18px;
  border-radius: 10px;
  background: var(--spotterRaised);
  border: 1px solid var(--spotterLine);
  color: var(--spotterInkSoft);
  font-size: 14px;
`;

/** Spinner + text on one line, for `StatusMessage`'s in-progress (still polling) state. */
const StatusMessageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InlineSpinner = styled(Spinner)`
  padding: 0;
`;

/** Completed-game matchup card. */
const MatchupCard = styled.div`
  border: 1px solid var(--spotterLine);
  border-radius: 13px;
  background: var(--spotterSurface);
  box-shadow: var(--spotterShadow);
  padding: 16px 18px;
  margin-bottom: 16px;
`;

const MatchupHead = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const WlBadge = styled.span<{ $result: "w" | "l" | "t" }>`
  font-family: ui-monospace, Menlo, monospace;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.06em;
  padding: 3px 9px;
  border-radius: 6px;
  background: ${(p) =>
    p.$result === "w"
      ? "var(--spotterOkSoft)"
      : p.$result === "l"
      ? "var(--spotterDangerSoft)"
      : "var(--spotterLine2)"};
  color: ${(p) =>
    p.$result === "w"
      ? "var(--spotterOk)"
      : p.$result === "l"
      ? "var(--spotterDanger)"
      : "var(--spotterInkSoft)"};
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: ui-monospace, Menlo, monospace;
  font-variant-numeric: tabular-nums;
`;

/* Explicit .code/.score classes (not :first-of-type/:last-of-type): the two sides deliberately
 * render in MIRRORED order below — "TEAM score – score TEAM", the standard linescore reading (my
 * team's code sits outside on the left, the opponent's code sits outside on the right, both scores
 * lead into the dash in the middle) — so DOM position no longer tells you which span is which. */
const TeamScore = styled.div<{ $me?: boolean }>`
  display: flex;
  align-items: baseline;
  gap: 8px;

  .code {
    font-weight: 700;
    font-size: 16px;
    letter-spacing: 0.03em;
    color: ${(p) => (p.$me ? "var(--spotterAccent)" : "var(--spotterInkSoft)")};
  }

  .score {
    font-size: 26px;
    font-weight: 700;
    line-height: 1;
  }

  /* Same lead/trail tone as the table's own ScoreCell (below) — whoever's ahead reads bright,
     trailing (and a scoreless 0-0, before anyone's scored) reads muted — Addison. */
  .score.lead {
    color: var(--spotterInk);
  }

  .score.trail {
    color: var(--spotterInkFaint);
  }
`;

/** Home/away + venue tag — Addison asked for this back after it got dropped along with the rest of
 *  the old second row: "if it's away then include the location... even if it's at their [home]
 *  stadium include the name in the tag" — so the venue always renders when known, not just for
 *  away games. */
const HomeAway = styled.span`
  font-size: 12.5px;
  color: var(--spotterInkSoft);
  font-family: ui-monospace, Menlo, monospace;
  background: var(--spotterCodeBg);
  padding: 3px 9px;
  border-radius: 6px;
`;

/** Pushes the game-id copy button (and, since round 26, the "Refresh gamebook" button beside it)
 *  to the far right of `MatchupHead`'s single row — Addison, round 25. The extra left padding
 *  gives it a bit of breathing room from the score line next to it. */
const GameIdSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  padding-left: 6px;
`;

/** "Not yet played" generate card. */
const GenRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

const GenInfo = styled.div`
  flex: 1;
  min-width: 240px;

  h3 {
    margin: 0 0 4px;
    font-size: 15px;
  }

  p {
    margin: 0;
    font-size: 13px;
    color: var(--spotterInkSoft);
    max-width: 62ch;
  }
`;

const OppLine = styled.div`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 13px;
  color: var(--spotterInk);
  margin-top: 6px;

  .pend {
    color: var(--spotterInkFaint);
  }
`;

/** Toolbar. */
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 11px 0;
  margin-bottom: 6px;
`;

const QuickChips = styled.div`
  display: inline-flex;
  border: 1px solid var(--spotterLine2);
  border-radius: 9px;
  overflow: hidden;
`;

const QuickChip = styled.button<{ $on?: boolean }>`
  background: ${(p) =>
    p.$on ? "var(--spotterAccent)" : "var(--spotterSurface)"};
  color: ${(p) => (p.$on ? "#14181d" : "var(--spotterInkSoft)")};
  border: 0;
  border-right: 1px solid var(--spotterLine2);
  /* A bit shorter than the search Input's own default height (39px) — full alignment with Input
     read as slightly too tall/heavy for a quick-filter chip. */
  height: 34px;
  padding: 0 13px;
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-variant-numeric: tabular-nums;

  &:last-child {
    border-right: 0;
  }

  &:hover {
    color: ${(p) => (p.$on ? "#14181d" : "var(--spotterAccent)")};
  }
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 180px;
  max-width: 320px;
`;

const Toggle = styled.button<{ $on?: boolean; $tone: "sc" | "pn" | "cr" }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  border-radius: 99px;
  border: 1px solid var(--spotterLine2);
  background: var(--spotterSurface);
  color: var(--spotterInkSoft);
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;

  &:hover {
    border-color: var(--spotterAccentLine);
  }

  ${(p) =>
    p.$on &&
    p.$tone === "sc" &&
    `color: var(--spotterOk); background: var(--spotterOkSoft); border-color: var(--spotterOk);`}
  ${(p) =>
    p.$on &&
    p.$tone === "pn" &&
    `color: var(--spotterFlag); background: var(--spotterFlagSoft); border-color: var(--spotterFlag);`}
  ${(p) =>
    p.$on &&
    p.$tone === "cr" &&
    `color: var(--spotterDanger); background: var(--spotterDangerSoft); border-color: var(--spotterDanger);`}
`;

const ToggleDot = styled.span<{ $on?: boolean; $tone: "sc" | "pn" | "cr" }>`
  width: 9px;
  height: 9px;
  border-radius: 99px;
  background: ${(p) =>
    p.$on
      ? p.$tone === "sc"
        ? "var(--spotterOk)"
        : p.$tone === "pn"
        ? "var(--spotterFlag)"
        : "var(--spotterDanger)"
      : "var(--spotterLine2)"};
`;

const Count = styled.div`
  margin-left: auto;
  font-size: 12.5px;
  color: var(--spotterInkSoft);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  b {
    color: var(--spotterInk);
    font-weight: 700;
  }
`;

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 9px;
  border: 1px solid var(--spotterAccentLine);
  background: var(--spotterAccentSoft);
  color: var(--spotterAccent);
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;

  &:hover {
    background: var(--spotterAccent);
    color: #14181d;
    border-color: var(--spotterAccent);
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

/** Column widths shared between `Colhead` (below) and `Table`'s own `columns` prop, so the two
 *  grids line up pixel-for-pixel. uid/situation/possession/play/score/flags — uid at 105px (100
 *  read a bit tight); score widened out (116→140, "DAL 24 – 17 NYG" was cramped) — Addison.
 *  `play` is `1fr` so it absorbs whatever the fixed columns don't use. */
const COLUMN_WIDTHS = ["105px", "240px", "104px", "1fr", "140px", "130px"];

/** Where the fixed-width `Colhead`/`Table` grid below actually stops being broken — NOT the
 *  `700px` every other Spotter screen in this file/app uses for its own "mobile" breakpoint (see
 *  `SpotterHeader.tsx`'s `MOBILE_MAX_WIDTH` doc comment). Measured live (`getBoundingClientRect`
 *  on a real row, across a sweep of viewport widths, same REG03 seeded data round 1/2/3 all use)
 *  rather than assumed, per the round-3 work order's own ask to confirm the mechanism before
 *  picking a fix:
 *
 *  | width | overflow | first row height |
 *  |-------|----------|-------------------|
 *  | 750px | 124px    | 1,161px            |
 *  | 850px | 24px     | 1,161px            |
 *  | 900px | 0px      | 225px              |
 *  | 980px | 0px      | 89px               |
 *  | 1000px| 0px      | 69.5px             |
 *  | 1100px| 0px      | 52px (= the intended `rowHeight` prop below)             |
 *
 *  Below ~900px the grid is still the CRITICAL bug (horizontal overflow AND multi-hundred-px
 *  rows) — it doesn't finish settling down to its own intended row height until ~1100px, well
 *  into tablet/small-laptop territory, because `COLUMN_WIDTHS`' five fixed columns (719px) need
 *  real room left over for the flexible `1fr` play-description column too, not just enough to
 *  avoid literal overflow. So `MobilePlayList` (below) has to cover that whole range, not just
 *  phone widths — this is the one Spotter breakpoint in the app that ISN'T 700px, because this is
 *  the one layout whose actual failure point isn't 700px either. */
const GAMEBOOK_TABLE_MIN_WIDTH_PX = 1100;

/** Extra left inset (on top of `TableRow`'s own normal 10px) reserved as a dedicated lane for
 *  `AccentBar`, applied via `Table`'s `rowPadding` prop below and matched on `Colhead`'s own left
 *  padding so the two grids stay aligned. NOT applied as padding on `TableCard`/`Section` itself —
 *  `Table`'s internal wrappers (`StyledTable`, `Scroll`) size themselves to `TableCard`'s CONTENT
 *  box, not its padding box, and both those wrappers are `overflow: hidden`/`auto`, so anything
 *  painted by a row descendant outside THEIR box (i.e. back in `TableCard`'s own padding) gets
 *  clipped regardless of what `TableCard`'s own larger box would otherwise allow — a real gutter
 *  has to live inside the row's own box instead. See `AccentBar`'s doc comment. */
const ACCENT_GUTTER = 10;
const ROW_LEFT_PADDING = 10 + ACCENT_GUTTER;

/** Standalone sticky column-header bar — NOT `Table`'s own built-in `<header>` (that one's
 *  `columns` no longer carry a `title`, so `Table` skips rendering it entirely; see the `Table`
 *  usage below). Round 20 (Addison, live-verify): the earlier attempt restyled `Table`'s own
 *  header in place and made THAT sticky, but it never actually stuck — `Table`'s `StyledTable` sets
 *  `overflow: hidden`, and `Section`'s own `StyledSection` does too, and BOTH sit
 *  between that header and the page's real scrolling context. An `overflow: hidden` ancestor clips
 *  `position: sticky` to its own box, and neither box has any scrollable area of its own here (both
 *  auto-size to fit their content) — so the header had nowhere to "stick" and just scrolled away
 *  with the rows, which read as it rendering statically partway down the page instead of pinned to
 *  the top. Rather than override `overflow` on two shared components from the outside (risking
 *  whatever else relies on that clipping elsewhere in the app) — or teach `Table` itself a
 *  sticky-header mode, which would mean changing how its `StyledTable`/`Scroll` overflow model
 *  works for every consumer of a widely-shared component for one screen's layout — this renders
 *  the header as its own sibling ABOVE `<TableCard>` (below), same approach the reference mock
 *  itself uses (`.colhead` is a separate sticky element, not part of any row-list wrapper), so
 *  there's no hidden-overflow ancestor between it and the page to fight.
 *
 *  Round 21: now styled as the TOP of the card (bordered, rounded top corners, `--spotterSurface`
 *  background matching `Section`'s own) rather than a borderless bar on the page background —
 *  `TableCard` (below) squares off `Section`'s own top corners and drops its top border so the two
 *  read as one continuous card; `Colhead` "owns" the rounded top edge and keeps it while sticky.
 *
 *  Round 22: a `border-radius` only rounds the shape actually painted — the tiny sliver of this
 *  element's bounding box OUTSIDE that rounded corner is never painted by `Colhead`'s own
 *  background at all, so once it's pinned, whatever row is scrolling past underneath at that exact
 *  screen position shows through the corner cutouts. `ColheadSticky` (below) is a plain, UNROUNDED
 *  rectangle in the page's own background color, sticky at the same offset, sitting BEHIND
 *  `Colhead` — it blocks anything scrolling past for the full rectangle regardless of the rounded
 *  shape on top, so the corners just read as "rounded against the page", not "rounded with things
 *  peeking through". */
/** \`top\` assumes \`SpotterHeader\` is pinned flush above at \`--spotterHeaderHeight\` — true
 *  whenever this actually renders, since \`Colhead\`/\`ColheadSticky\` only mount above
 *  \`GAMEBOOK_TABLE_MIN_WIDTH_PX\` (1100px) now, comfortably past \`SpotterHeader\`'s own 700px
 *  static-on-mobile breakpoint — so, unlike round 1's original fix here, there's no width where
 *  this renders AND the header has gone \`position: static\` out from under it. Round 1 added a
 *  \`@media (max-width: 700px) { top: 0 }\` fallback for exactly that case; round 3 removes it as
 *  dead code — it can no longer fire now that this component's own mount condition already
 *  excludes every width where the header isn't sticky. */
const ColheadSticky = styled.div`
  position: sticky;
  top: var(--spotterHeaderHeight, 98px);
  z-index: 20;
  background: var(--spotterPaper);
`;

const Colhead = styled.div`
  display: grid;
  /* Matches Table's own row exactly (column-gap: 10px; padding: 5px 10px) — not the
     mock's 12px/14px numbers, which were sized for its own hand-rolled row markup — so these
     labels land directly above the real row cells instead of drifting out of alignment with them.
     Left padding matches ROW_LEFT_PADDING (the rowPadding passed to Table below, which reserves
     AccentBar's lane) so a row's grid still starts at the same x-position as this header's despite
     that extra inset. */
  grid-template-columns: ${COLUMN_WIDTHS.join(" ")};
  gap: 10px;
  align-items: center;
  background: var(--spotterSurface);
  border: 1px solid var(--spotterLine);
  border-radius: 12px 12px 0 0;
  padding: 10px 10px 10px ${ROW_LEFT_PADDING}px;
  font-size: 10.5px;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: var(--spotterInkFaint);
  font-family: ui-monospace, Menlo, monospace;
  font-weight: 700;
`;

/** Squares off `Section`'s own top corners and drops its top border, since the sticky `Colhead`
 *  above it now owns that rounded top edge — together they read as one continuous card, the way
 *  `Table`'s own (non-sticky) header + row list used to. */
const TableCard = styled(Section)`
  border-radius: 0 0 12px 12px !important;
  border-top: 0 !important;
`;

/** Below `GAMEBOOK_TABLE_MIN_WIDTH_PX`, the fixed-width `Colhead`/`Table` grid above (719px across
 *  five fixed columns, no responsive handling at all) is swapped for this stacked-card-per-play
 *  layout instead — well under that width the grid doesn't just overflow, `PlayCell`'s deliberate
 *  wrap-instead-of-truncate text fighting the squeezed columns for room balloons each row to
 *  ~1,730px tall (see PR #362 round 3's investigation, and `GAMEBOOK_TABLE_MIN_WIDTH_PX`'s own
 *  doc comment for the measurements that picked 1100px specifically). Reuses the exact same
 *  per-field content as the desktop grid
 *  (`situationCellContent`/`possessionCellContent`/etc., and the `UidCell`/`SitCell`/`PossCell`/
 *  `PlayCell`/`ScoreCell`/`FlagsCell`/`PlayDetail` components themselves) — only the outer shape
 *  changes from one grid row to a bordered card with a small uppercase label above each field, so
 *  the info still reads clearly with no column header in view to give it context. Mirrors
 *  `MatchupCard`'s own border/radius/shadow recipe (this file, above) rather than inventing a new
 *  card look.
 *
 *  A JS breakpoint check (`useMediaQuery`, not a plain `@media` + `display: none` swap) decides
 *  which of this and the desktop `Table` actually MOUNTS — the desktop `Table` isn't virtualized,
 *  so a real game's play list (150+ rows) is already fully in the DOM on desktop; hiding one
 *  variant with CSS instead of not rendering it at all would double that DOM cost on every mobile
 *  visit for no benefit (nobody ever sees the hidden one). */
const MobilePlayList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/** One play's mobile card — `$isOpen` mirrors the desktop row's own inset-ring "expanded" look
 *  (`rows` below, in `GamebookScreen`) rather than `Table`'s built-in `.active` background swap,
 *  same reasoning as the desktop row's own comment on that choice. */
const PlayCard = styled.div<{ $isOpen?: boolean }>`
  position: relative;
  border: 1px solid var(--spotterLine);
  border-radius: 12px;
  background: var(--spotterSurface);
  box-shadow: ${(p) =>
    p.$isOpen
      ? "inset 0 0 0 1px var(--spotterAccentLine)"
      : "var(--spotterShadow)"};
  padding: 12px 14px 12px 24px;
  cursor: pointer;
`;

/** Spacing between the card's UID line and the first labeled field below it — kept separate from
 *  `UidCell` itself (shared with the desktop grid row, where this margin would be wrong). */
const PlayCardHead = styled.div`
  margin-bottom: 8px;
`;

const PlayCardField = styled.div`
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PlayCardLabel = styled.div`
  font-size: 10.5px;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: var(--spotterInkFaint);
  font-family: ui-monospace, Menlo, monospace;
  font-weight: 700;
  margin-bottom: 3px;
`;

/** Play row cells. */
const UidCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  b {
    font-family: ui-monospace, Menlo, monospace;
    font-weight: 700;
    font-size: 14px;
    color: var(--spotterAccent);
    letter-spacing: 0.02em;
  }

  .pid {
    font-family: ui-monospace, Menlo, monospace;
    font-size: 10.5px;
    color: var(--spotterInkFaint);
  }

  /* Copy button starts hidden (CopyButton's own hoverOnly styling) — reveal it on hover
     ANYWHERE in this cell, not just while hovering the tiny icon itself. */
  &:hover .copy-btn {
    opacity: 1;
  }
`;

const SitCell = styled.div`
  font-size: 12.5px;
  color: var(--spotterInkSoft);
  font-variant-numeric: tabular-nums;

  .q {
    font-family: ui-monospace, Menlo, monospace;
    font-weight: 700;
    color: var(--spotterInk);
    font-size: 12px;
    background: var(--spotterCodeBg);
    padding: 2px 6px;
    border-radius: 5px;
    margin-right: 7px;
  }

  .dd {
    color: var(--spotterInk);
    font-weight: 600;
  }
`;

const PossCell = styled.div`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12.5px;
  color: var(--spotterInkSoft);
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;

  b {
    color: var(--spotterInk);
  }

  .arrow {
    display: inline-flex;
    align-items: center;
    color: var(--spotterInkFaint);
  }
`;

const PlayCell = styled.div`
  font-size: 13px;
  /* One step dimmer than --spotterInk (the row's other bright text, e.g. UID/scores) — Addison:
     the description read a little too bright against the row surface. Not --spotterInkSoft, which
     is two steps down and already used for secondary/muted text elsewhere in this row. */
  color: var(--gray300);
  /* Full text, no truncation — a play's description is never very long, so let it wrap onto a
     second line rather than clipping it with an ellipsis. */
  white-space: normal;
  overflow-wrap: break-word;

  .yd {
    font-family: ui-monospace, Menlo, monospace;
    font-weight: 700;
    font-size: 13.5px;
    margin-right: 8px;
  }

  .yd.pos {
    color: var(--spotterOk);
  }

  .yd.neg {
    color: var(--spotterDanger);
  }

  .yd.zero {
    color: var(--spotterInkFaint);
  }
`;

const ScoreCell = styled.div`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 13px;
  text-align: left;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  /* Whoever's ahead reads bright/bold; trailing (and an 0-0 tie, before anyone's scored) reads
     muted. A NON-zero tie is bright on both sides — "the last score-changing play kept it level"
     is worth calling out, unlike a scoreless tie, which is just "nothing's happened yet". */
  .lead {
    color: var(--spotterInk);
    font-weight: 700;
  }

  .trail {
    color: var(--spotterInkFaint);
  }
`;

const FlagsCell = styled.div`
  display: flex;
  gap: 5px;
  justify-content: flex-start;
  align-items: center;
`;

/** Expanded-row detail panel — a label-left/value-right row per field, mirroring
 *  `apps/frontend/src/features/shared/VariableInput.tsx`'s `FieldRow` (the same pattern
 *  iconik-manager's own plugin/script "configure variables" screen uses), per Addison's own
 *  reference point, rather than the previous auto-fit label-above-value card grid. Deliberately
 *  narrower than the mock's full `detail()` field set: `Play ID`, `Line of scrimmage`,
 *  `On offense → defense`, `Offensive play elements`, and `Full summary` are all OMITTED here
 *  because they're already fully visible in the collapsed row itself (situation cell, renamed
 *  Off/Def column, and — since `PlayCell` no longer truncates — the complete play description) —
 *  repeating them below would just restate the same row twice. `Importance` leads since that's
 *  the one field an analyst is most likely to scan the expanded rows FOR. */
const Detail = styled.div`
  grid-column: 1 / -1;
  margin-top: 9px;
  padding-top: 2px;
  border-top: 1px solid var(--spotterLine);
  /* cursor inherits down from the row (Table's own link styling sets cursor: pointer on the whole
     TableRow since it has an onClick) — but this panel doesn't toggle on click (see the onClick
     stopPropagation above), so a pointer cursor here would be misleading. */
  cursor: default;
`;

const DRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2.5fr;
  gap: 4px 14px;
  padding: 7px 2px;
  border-bottom: 1px solid var(--spotterLine);
  /* Label and value line up on their text baseline, not the top of each box. */
  align-items: baseline;

  &:last-child {
    border-bottom: 0;
  }

  .lab {
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    text-align: right;
    color: var(--spotterInkFaint);
    font-family: ui-monospace, Menlo, monospace;
  }

  .val {
    font-size: 13px;
    color: var(--spotterInk);
  }
`;

const DTagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const DTag = styled.span`
  font-size: 11.5px;
  font-family: ui-monospace, Menlo, monospace;
  background: var(--spotterCodeBg);
  border: 1px solid var(--spotterLine2);
  border-radius: 6px;
  padding: 2px 8px;
  color: var(--spotterInkSoft);
`;

/** Which side of a play's score reads "lead" (bright) vs "trail" (muted). A 0-0 score is treated
 *  as "nothing's happened yet" (both muted), not "tied" (both bright) — only a NON-zero tie (the
 *  scoreboard was actually even at some point after both teams had scored) gets the brighter
 *  "tied" treatment on both sides. */
const scoreTone = (
  away: number,
  home: number
): { away: "lead" | "trail"; home: "lead" | "trail" } => {
  if (away === home) {
    return away === 0
      ? { away: "trail", home: "trail" }
      : { away: "lead", home: "lead" };
  }
  return away > home
    ? { away: "lead", home: "trail" }
    : { away: "trail", home: "lead" };
};

/** Omits the row entirely when there's nothing to show (rather than a placeholder "—") — a play
 *  with no penalty, no pre-snap elements recorded, etc. just doesn't get that field listed. */
const detailRow = (
  label: string,
  value: React.ReactNode,
  valueColor?: string
): React.ReactNode => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  return (
    <DRow>
      <div className="lab">{label}</div>
      <div
        className="val"
        style={valueColor ? { color: valueColor } : undefined}
      >
        {value}
      </div>
    </DRow>
  );
};

/** Importance reads as color-coded text rather than a flat default — "Critical" in the same red as
 *  a KEY flag, "Important" in the same amber/yellow as a penalty flag, "Routine" in green (nothing
 *  to flag = the good outcome). Anything unset/unrecognized is left at the panel's default color. */
const importanceTone = (value: string | null): string | undefined => {
  const v = (value ?? "").toLowerCase();
  if (v === "critical") {
    return "var(--spotterDanger)";
  }
  if (v === "important") {
    return "var(--spotterFlag)";
  }
  if (v === "routine") {
    return "var(--spotterOk)";
  }
  return undefined;
};

/** A real painted left-edge accent bar, NOT a CSS `border`/`box-shadow` on the row itself — those
 *  either shift the row's grid content (a border eats into the padding box, moving where the grid
 *  starts) or need the row's own corner-rounding to clip cleanly. `position: absolute` takes this
 *  OUT of grid layout entirely (an absolutely-positioned child isn't a grid item, so it can't
 *  affect column sizing/position at all) and it never rounds off, even when the row it sits in
 *  gets a rounded corner for being expanded — it's a separate element with no radius of its own.
 *
 *  Addison (round 23): this used to sit at `left: 0` — exactly on the row's own edge, which is also
 *  exactly where `TableCard`'s left border is, so the bar visually read as painted onto (or as part
 *  of) the card's own border rather than a distinct marker outside the row/table content. The fix
 *  is NOT a negative offset escaping `TableRow`'s own box — `Table`'s internal wrappers
 *  (`StyledTable`/`Scroll`) are `overflow: hidden`/`auto` themselves and sit between `TableCard` and
 *  every row, so anything painted outside a row's own box gets clipped by those regardless of
 *  `TableCard`'s own (larger) box. Instead, `<Table rowPadding=... />` below reserves an extra
 *  `ACCENT_GUTTER`-px lane INSIDE each row's own left padding (matched on `Colhead`'s padding so the
 *  grids stay aligned) — `left` here just centers the bar within that lane, entirely within
 *  `TableRow`'s own box, with a visible gap to the card's border on one side and the real cell
 *  content on the other, so it reads as outside the table's content without needing to escape any
 *  ancestor's clipping at all.
 *
 *  Addison (round 24): rows sit flush against each other again (no `marginBottom`) — instead the
 *  bar itself is inset `ACCENT_BAR_VINSET` from `TableRow`'s own top/bottom edges, shorter than the
 *  full row height, so two consecutive accented rows still read as two distinct marks with a real
 *  gap between them even though the rows themselves touch. */
const ACCENT_BAR_WIDTH = 3;
const ACCENT_BAR_LEFT = (ACCENT_GUTTER - ACCENT_BAR_WIDTH) / 2;
const ACCENT_BAR_VINSET = 8;

const AccentBar = styled.span<{ $color: string }>`
  position: absolute;
  left: ${ACCENT_BAR_LEFT}px;
  top: ${ACCENT_BAR_VINSET}px;
  bottom: ${ACCENT_BAR_VINSET}px;
  width: ${ACCENT_BAR_WIDTH}px;
  border-radius: ${ACCENT_BAR_WIDTH}px;
  background: ${(p) => p.$color};
`;

/** Which color (if any) a play's row gets as a left-edge accent — at most one wins when a play
 *  qualifies for more than one. Priority: a scoring play is the most useful thing to spot at a
 *  glance while scanning a long list, "key"/critical-importance plays next, penalties last (the
 *  most common/administrative of the three, and least likely to be what someone's scanning FOR). */
function rowAccent(scored: boolean, play: GamebookPlay): string | undefined {
  if (scored) {
    return "var(--spotterOk)";
  }
  if (play.playImportance?.toLowerCase() === "critical") {
    return "var(--spotterDanger)";
  }
  if (play.penaltyType) {
    return "var(--spotterFlag)";
  }
  return undefined;
}

/** Comma-delimited elements (mock's own `tags()` helper) rendered as a wrapping chip row. */
const tagList = (value: string | null): React.ReactNode => {
  const tags = (value ?? "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
  if (!tags.length) {
    return null;
  }
  return (
    <DTagRow>
      {tags.map((tag) => (
        <DTag key={tag}>{tag}</DTag>
      ))}
    </DTagRow>
  );
};

function csvCell(value: string | number | null | undefined): string {
  const s = value == null ? "" : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Column order/keys mirror the reference mock's own `downloadCSV()` verbatim (snake_case,
 *  `master_play_by_play`-shaped) — this is a raw data export for someone piping it into another
 *  tool, not user-facing prose, so it keeps the source-system column naming rather than this
 *  app's camelCase house style. */
const CSV_PLAY_COLUMNS: Array<{
  key: string;
  get: (play: GamebookPlay) => string | number | null;
}> = [
  { key: "play_id", get: (p) => p.playId },
  { key: "quarter", get: (p) => p.quarter },
  { key: "game_clock", get: (p) => p.gameClock },
  { key: "down", get: (p) => p.down },
  { key: "distance_to_go", get: (p) => p.distanceToGo },
  { key: "line_of_scrimmage", get: (p) => p.lineOfScrimmage },
  { key: "team_on_offense", get: (p) => p.teamOnOffense },
  { key: "team_on_defense", get: (p) => p.teamOnDefense },
  { key: "away_score", get: (p) => p.awayScore },
  { key: "home_score", get: (p) => p.homeScore },
  { key: "yards_gained_or_lost", get: (p) => p.yardsGainedOrLost },
  { key: "offensive_play_elements", get: (p) => p.offensivePlayElements },
  { key: "presnap_elements", get: (p) => p.presnapElements },
  { key: "penalty_type", get: (p) => p.penaltyType },
  { key: "penalty_team", get: (p) => p.penaltyTeam },
  { key: "play_importance", get: (p) => p.playImportance },
  { key: "time_of_day", get: (p) => p.timeOfDay },
  { key: "mechanical_summary", get: (p) => p.mechanicalSummary },
];

/** Downloads the CURRENTLY SHOWN plays (whatever quarter/search/scoring/penalty filters are
 *  active) as CSV — `filenameSuffix` records which filters were active in the filename itself,
 *  same as the reference mock. */
function downloadGamebookCsv(
  game: GamebookCompletedGame,
  plays: GamebookPlay[],
  filenameSuffix: string
): void {
  const headers = [
    "game_id",
    "season",
    "week",
    "away_team",
    "home_team",
    "uid",
    "play_uid_full",
    ...CSV_PLAY_COLUMNS.map((c) => c.key),
  ];
  const rows = plays.map((p) => [
    game.gameId,
    game.season,
    game.week,
    game.away,
    game.home,
    p.uid,
    `${game.gameId}_${p.uid}`,
    ...CSV_PLAY_COLUMNS.map((c) => c.get(p)),
  ]);
  // Leading BOM so Excel opens it as UTF-8 instead of guessing a different codepage.
  const csv =
    "﻿" +
    [headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `spotter_gamebook_${game.gameId}${filenameSuffix}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Per-field cell content, factored out of the desktop row map (below) so the mobile card list
 *  (`MobilePlayList`) renders the exact same markup for each field instead of a second,
 *  independently-maintained copy — the two layouts differ only in how these get arranged
 *  (grid columns vs. labeled stacked blocks), never in what they say.
 *
 *  `situationCellContent`/`playCellContent`/`isCriticalPlay`/`flagsCellContent`/`hasAnyFlag`/`Fl`
 *  live in `./gamebookCellContent.tsx` so the Films Ground anchor editor's gamebook band can
 *  reuse them verbatim — imported below, unchanged in behavior. */
const possessionCellContent = (play: GamebookPlay): React.ReactNode => (
  <>
    <b>{play.teamOnOffense}</b>
    <span className="arrow">⏵</span>
    {play.teamOnDefense}
  </>
);

const scoreCellContent = (
  play: GamebookPlay,
  game: GamebookCompletedGame | undefined,
  tone: { away: "lead" | "trail"; home: "lead" | "trail" }
): React.ReactNode => (
  <>
    <span className={tone.away}>
      {game?.away} {play.awayScore}
    </span>{" "}
    <span className="trail">–</span>{" "}
    <span className={tone.home}>
      {play.homeScore} {game?.home}
    </span>
  </>
);

const PlayDetail = ({ play }: { play: GamebookPlay }) => {
  const t = SPOTTER_STRINGS.gamebook.detail;
  return (
    // Stops the click from bubbling up to the row's own `onClick` (Table.tsx renders this INSIDE
    // the same TableRow as the collapsed cells, so without this, clicking anywhere in the detail
    // panel — not just the row above it — also toggled the row) — Addison: only the actual
    // (collapsed) row should expand/contract on click, not the expanded panel below it.
    <Detail onClick={(e) => e.stopPropagation()}>
      {detailRow(
        t.importance,
        play.playImportance,
        importanceTone(play.playImportance)
      )}
      {/* Raw source values, not humanized — Addison: they need to know exactly what the source
          will map to, e.g. "1" (not "First down"), "5" (not "5 to go"). */}
      {detailRow(t.down, play.down)}
      {detailRow(t.distanceToGo, play.distanceToGo)}
      {detailRow(t.lineOfScrimmage, play.lineOfScrimmage)}
      {detailRow(
        t.penalty,
        play.penaltyType
          ? `${play.penaltyType}${
              play.penaltyTeam ? ` (${play.penaltyTeam})` : ""
            }`
          : null
      )}
      {detailRow(t.presnap, tagList(play.presnapElements))}
    </Detail>
  );
};

/** Addison: the season/week picker should remember what you were last looking at across a
 *  reload, not always reset to the club's current week. Plain `localStorage` (not react-query
 *  cache/URL state) — this is purely a per-browser UI convenience, not server state and not
 *  something worth putting in the URL for this single-view tab. */
const SEASON_STORAGE_KEY = "spotter-gamebook-season";
const WEEK_STORAGE_KEY = "spotter-gamebook-week";

/** Cap on how long a triggered generation keeps auto-polling before giving up and falling back to
 *  "refresh to check on it" — the `/matchup` response has no "failed" status of its own (Phase 1
 *  runs out-of-band in n8n), so without a cap a generation that never finishes would poll forever.
 *  Generous relative to the "a few minutes" copy in `generateBody`/`generateSuccess`. */
const GENERATE_POLL_TIMEOUT_MS = 10 * 60 * 1000;

/** `generateMutation`'s error banner text — the mutation's own `ApiError.message` for a genuine
 *  failure, but never a stale "Unauthorized" flash for a 401: this whole screen is about to be
 *  superseded by `useInterfaceAccess`'s "denied" notice anyway (a 401 anywhere dispatches
 *  `SESSION_EXPIRED_EVENT`, which every mounted `useInterfaceAccess` picks up — see its doc
 *  comment), and it's a less useful message than the feature-specific fallback in the split-second
 *  before that happens. Also falls back on an empty `.message` (a non-JSON proxy error carries
 *  none) so the banner is never blank. */
export function mutationErrorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError && error.status !== 401 && error.message
    ? error.message
    : fallback;
}

const GamebookScreen = () => {
  const access = useInterfaceAccess(SPOTTER_INTERFACE_ID);
  // Decides whether `MobilePlayList` or the desktop `Table` actually mounts below — see
  // `MobilePlayList`'s doc comment for why this is a JS check instead of a CSS-only swap.
  const isMobile = useMediaQuery(
    `(max-width: ${GAMEBOOK_TABLE_MIN_WIDTH_PX}px)`
  );
  const configQuery = useGamebookConfig();
  const config = configQuery.data;

  const [season, setSeasonState] = useState<number | undefined>(() => {
    const stored = localStorage.getItem(SEASON_STORAGE_KEY);
    return stored ? Number(stored) : undefined;
  });
  const [week, setWeekState] = useState<string | undefined>(
    () => localStorage.getItem(WEEK_STORAGE_KEY) ?? undefined
  );
  const setSeason = (value: number) => {
    setSeasonState(value);
    localStorage.setItem(SEASON_STORAGE_KEY, String(value));
  };
  const setWeek = (value: string) => {
    setWeekState(value);
    localStorage.setItem(WEEK_STORAGE_KEY, value);
  };
  // Only used as a fallback for a first-ever visit (nothing in localStorage yet) — once season/week
  // are set once, they're persisted above and this never fires again.
  useEffect(() => {
    if (config && season === undefined && week === undefined) {
      setSeason(config.currentSeason);
      setWeek(config.currentWeek);
    }
  }, [config, season, week]);

  const weeksQuery = useGamebookWeeks(season);
  // Which (season, week) a "Generate gamebook" call is pending for, if any — keyed explicitly
  // rather than inferred from `generateMutation.isSuccess` (a single shared flag that doesn't
  // know which week it was for) so that: (1) switching weeks before the mutation resolves polls
  // the WEEK THAT WAS ACTUALLY GENERATED, not whatever's on screen when the response lands, and
  // (2) navigating away from that week and back RESUMES tracking it instead of only polling for
  // as long as the customer happens to stay put. `isPollingTarget` (below, after `matchup`'s
  // declared) is true only while the picker is actually showing this pending week; `matchupQuery`
  // only polls then, so a week nothing was triggered on never gets spuriously polled.
  const [pendingGeneration, setPendingGeneration] = useState<{
    season: number;
    week: string;
    startedAt: number;
    /** `game.lastGeneratedAt` at the moment THIS refresh was triggered, captured only when
     *  triggering from an ALREADY-completed game's "Refresh gamebook" button — `status` stays
     *  `"completed"` throughout a refresh of a completed game, so it can't signal "done" by
     *  itself the way a scheduled -> completed transition can. `undefined` for a first-time
     *  generate (the scheduled card), where that status transition IS the completion signal. */
    baselineGeneratedAt?: string | null;
  } | null>(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const isPollingTarget =
    !!pendingGeneration &&
    pendingGeneration.season === season &&
    pendingGeneration.week === week;
  const matchupQuery = useGamebookMatchup(
    season,
    week,
    isPollingTarget && !pollTimedOut
  );
  const generateMutation = useGenerateGamebook(season ?? 0, week ?? "");

  const [selectPortal, setSelectPortal] = useState<HTMLDivElement | null>(null);
  const [quarterFilter, setQuarterFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [scoringOnly, setScoringOnly] = useState(false);
  const [penaltiesOnly, setPenaltiesOnly] = useState(false);
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [openRows, setOpenRows] = useState<Set<string>>(new Set());

  // Reset per-game UI state (filters, expanded rows) whenever the selected week changes — mirrors
  // the reference mock's `resetFilters()` behavior.
  useEffect(() => {
    setQuarterFilter("all");
    setSearch("");
    setScoringOnly(false);
    setPenaltiesOnly(false);
    setCriticalOnly(false);
    setOpenRows(new Set());
  }, [season, week]);

  const matchup = matchupQuery.data;
  const game = matchup?.status === "completed" ? matchup.game : undefined;
  // Only meaningful once `matchup.status === "completed"` — read out here (rather than inline in
  // the effect below) so it can sit in that effect's own dependency array directly.
  const completedGeneratedAt = game?.lastGeneratedAt;

  // Stop tracking once the pending generation is actually done, or once GENERATE_POLL_TIMEOUT_MS
  // has passed with no luck (defense in depth — n8n's completion callback may not be wired up for
  // every org yet, so a generation that never signals "done" would otherwise poll forever). A
  // first-time generate (no `baselineGeneratedAt`) is done as soon as `status` reaches
  // `"completed"`, same as before. A REFRESH of an already-completed game stays `"completed"`
  // throughout, so it's only done once `lastGeneratedAt` moves past the value captured at click
  // time — n8n's callback is what supplies that. Re-runs on every poll tick (`dataUpdatedAt` for a
  // successful fetch, `errorUpdatedAt` so a run of FAILED polls — e.g. a backend blip — still gets
  // timed out on schedule) AND as soon as `isPollingTarget` flips true (the customer navigates
  // back to the pending week), so a generation that finished — or timed out — while off on another
  // week is caught immediately instead of waiting for the next tick.
  useEffect(() => {
    if (!isPollingTarget || !pendingGeneration) {
      return;
    }
    if (matchup?.status === "completed") {
      const isRefreshOfCompleted =
        pendingGeneration.baselineGeneratedAt !== undefined;
      const done =
        !isRefreshOfCompleted ||
        (!!completedGeneratedAt &&
          completedGeneratedAt !== pendingGeneration.baselineGeneratedAt);
      if (done) {
        setPendingGeneration(null);
        setPollTimedOut(false);
        return;
      }
    }
    if (Date.now() - pendingGeneration.startedAt > GENERATE_POLL_TIMEOUT_MS) {
      setPollTimedOut(true);
    }
  }, [
    isPollingTarget,
    pendingGeneration,
    matchup?.status,
    completedGeneratedAt,
    matchupQuery.dataUpdatedAt,
    matchupQuery.errorUpdatedAt,
  ]);

  /** Blocks a second trigger while one's already confirmed in flight for the CURRENT (season,
   *  week) — Addison: "don't allow multiple refreshes at once". `generateMutation.isLoading` alone
   *  only covers the initial POST's own round trip; this also covers the (much longer) window
   *  while Phase 1 itself is still running out-of-band in n8n. Applies to both the scheduled
   *  card's "Generate" button and the completed card's "Refresh" button below — same gap, same
   *  guard, either way the button reaches n8n. */
  const isGenerationInFlight = isPollingTarget && !pollTimedOut;

  const playsWithScoreFlag = useMemo(() => {
    if (!game) {
      return [];
    }
    // `computeScoredIndices` (`./gamebookCellContent.tsx`) — lifted so the anchor editor's
    // gamebook band computes the identical "did the score change on this play" flag over its own
    // full-game list, rather than re-deriving the rule.
    const scoredIdx = computeScoredIndices(game.plays);
    return game.plays.map((p, i) => ({ play: p, scored: scoredIdx.has(i) }));
  }, [game]);

  const quarters = useMemo(
    () =>
      Array.from(new Set(playsWithScoreFlag.map((p) => p.play.quarter))).sort(
        (a, b) => a - b
      ),
    [playsWithScoreFlag]
  );

  // Quarter/search only — NOT the scoring/penalties/critical toggles — so the toggles' own counts
  // (below) can be computed against "everything the quarter/search filters allow" independent of
  // which toggles happen to be on.
  const quarterFiltered = playsWithScoreFlag.filter(
    ({ play }) =>
      quarterFilter === "all" || String(play.quarter) === quarterFilter
  );
  const q = search.trim();
  // Fuzzy search — same `Fuse` approach as `CandidateAssignmentsScreen.tsx`'s own `fuseFilter` —
  // over the play's uid/summary/elements text. `shouldSort: false` keeps the list in its original
  // play order rather than Fuse's relevance-score order, which the table below (and the toggle
  // counts, computed from this same `baseFiltered` list) both assume.
  const baseFiltered = q
    ? new Fuse(quarterFiltered, {
        keys: [
          "play.uid",
          "play.mechanicalSummary",
          "play.offensivePlayElements",
        ],
        threshold: 0.4,
        shouldSort: false,
      })
        .search(q)
        .map((r) => r.item)
    : quarterFiltered;

  const filtered = baseFiltered.filter(({ play, scored }) => {
    if (scoringOnly && !scored) {
      return false;
    }
    if (penaltiesOnly && !play.penaltyType) {
      return false;
    }
    if (criticalOnly && play.playImportance?.toLowerCase() !== "critical") {
      return false;
    }
    return true;
  });

  /** A toggle's own displayed count reflects the OTHER currently-active toggles too (but never
   *  itself) — Addison: "if I select scoring and then there's only one critical score, the number
   *  of critical should reflect that". So each count = how many of the quarter/search-filtered
   *  plays would remain if you turned this toggle on, given whatever the other toggles are doing. */
  const otherTogglesMatch = (
    exclude: "scoring" | "penalties" | "critical",
    scored: boolean,
    play: GamebookPlay
  ): boolean => {
    if (scoringOnly && exclude !== "scoring" && !scored) {
      return false;
    }
    if (penaltiesOnly && exclude !== "penalties" && !play.penaltyType) {
      return false;
    }
    if (
      criticalOnly &&
      exclude !== "critical" &&
      play.playImportance?.toLowerCase() !== "critical"
    ) {
      return false;
    }
    return true;
  };

  const scoringCount = baseFiltered.filter(
    ({ play, scored }) => scored && otherTogglesMatch("scoring", scored, play)
  ).length;
  const penaltyCount = baseFiltered.filter(
    ({ play, scored }) =>
      !!play.penaltyType && otherTogglesMatch("penalties", scored, play)
  ).length;
  const criticalCount = baseFiltered.filter(
    ({ play, scored }) =>
      play.playImportance?.toLowerCase() === "critical" &&
      otherTogglesMatch("critical", scored, play)
  ).length;

  const toggleRow = (uid: string) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  };

  // Guarded by `isMobile` so the unused layout's ~150-row array isn't rebuilt on every render —
  // only the DOM mount was being skipped before, not this construction work.
  const rows: TableRow[] = isMobile
    ? []
    : filtered.map(({ play, scored }) => {
        const tone = scoreTone(play.awayScore, play.homeScore);
        const accent = rowAccent(scored, play);
        const isOpen = openRows.has(play.uid);
        // The expand ring is an INSET box-shadow, not a real border — a real border adds to the box's
        // own width, eating into the row's padding and shifting its grid columns out of alignment with
        // every other row (and the header). `AccentBar` (below) is a real painted element, but
        // `position: absolute` takes it OUT of grid layout entirely and out of the row's own content
        // box into its own reserved left-padding lane — see its doc comment.
        return {
          id: play.uid,
          onClick: () => toggleRow(play.uid),
          // Not using `Row.active` (Table's own `.active` styling swaps the row's BACKGROUND, which
          // reads fine for single-selection lists but not for "however many of these are expanded at
          // once" — Addison, round 20: don't change the row's background on expand, only its border.
          style: {
            boxShadow: isOpen
              ? "inset 0 0 0 1px var(--spotterAccentLine)"
              : undefined,
            borderRadius: isOpen ? "10px" : undefined,
            marginBottom: "1px",
          },
          element: () => (
            <>
              {accent && <AccentBar $color={accent} />}
              <UidCell>
                <b>{play.uid}</b>
                <span className="pid">#{play.playId}</span>
                {game && (
                  <CopyButton
                    value={`${game.gameId}_${play.uid}`}
                    title="Copy full play identifier"
                    hoverOnly
                    size={20}
                    iconSize={11}
                  />
                )}
              </UidCell>
              <SitCell>{situationCellContent(play)}</SitCell>
              <PossCell>{possessionCellContent(play)}</PossCell>
              <PlayCell>{playCellContent(play)}</PlayCell>
              <ScoreCell>{scoreCellContent(play, game, tone)}</ScoreCell>
              <FlagsCell>{flagsCellContent(scored, play)}</FlagsCell>
              {openRows.has(play.uid) && <PlayDetail play={play} />}
            </>
          ),
        };
      });

  /** Mobile-only stacked-card counterpart to `rows` above — same source data (`filtered`), same
   *  per-field content (the `*CellContent` helpers), just a different outer shape. See
   *  `MobilePlayList`'s doc comment for why this is a JS branch (`isMobile`, below) rather than a
   *  second always-mounted tree hidden with CSS. */
  const playCards = !isMobile
    ? []
    : filtered.map(({ play, scored }) => {
        const tone = scoreTone(play.awayScore, play.homeScore);
        const accent = rowAccent(scored, play);
        const isOpen = openRows.has(play.uid);
        return (
          <PlayCard
            key={play.uid}
            $isOpen={isOpen}
            onClick={() => toggleRow(play.uid)}
          >
            {accent && <AccentBar $color={accent} />}
            <PlayCardHead>
              <UidCell>
                <b>{play.uid}</b>
                <span className="pid">#{play.playId}</span>
                {game && (
                  // Not `hoverOnly` here (unlike the desktop row above) — touch has no reliable
                  // hover state to reveal it, so it stays visible.
                  <CopyButton
                    value={`${game.gameId}_${play.uid}`}
                    title="Copy full play identifier"
                    size={24}
                    iconSize={12}
                  />
                )}
              </UidCell>
            </PlayCardHead>
            <PlayCardField>
              <PlayCardLabel>
                {SPOTTER_STRINGS.gamebook.columns.situation}
              </PlayCardLabel>
              <SitCell>{situationCellContent(play)}</SitCell>
            </PlayCardField>
            <PlayCardField>
              <PlayCardLabel>
                {SPOTTER_STRINGS.gamebook.columns.possession}
              </PlayCardLabel>
              <PossCell>{possessionCellContent(play)}</PossCell>
            </PlayCardField>
            <PlayCardField>
              <PlayCardLabel>
                {SPOTTER_STRINGS.gamebook.columns.play}
              </PlayCardLabel>
              <PlayCell>{playCellContent(play)}</PlayCell>
            </PlayCardField>
            <PlayCardField>
              <PlayCardLabel>
                {SPOTTER_STRINGS.gamebook.columns.score}
              </PlayCardLabel>
              <ScoreCell>{scoreCellContent(play, game, tone)}</ScoreCell>
            </PlayCardField>
            {hasAnyFlag(scored, play) && (
              <PlayCardField>
                <PlayCardLabel>
                  {SPOTTER_STRINGS.gamebook.columns.flags}
                </PlayCardLabel>
                <FlagsCell>{flagsCellContent(scored, play)}</FlagsCell>
              </PlayCardField>
            )}
            {isOpen && <PlayDetail play={play} />}
          </PlayCard>
        );
      });

  // Org-level entitlement gate — see FieldsScreen.tsx's equivalent doc comment. Checked before
  // `configQuery.isError` below (a genuine load failure, a different concern).
  if (access.status !== "granted") {
    return (
      <Page>
        <SpotterHeader />
        {access.status === "denied" && (
          <Wrap>
            <InterfaceAccessNotice reason={access.reason} />
          </Wrap>
        )}
        {access.status === "pending" && <DelayedSpinner padTop />}
      </Page>
    );
  }

  const isLoading =
    configQuery.isLoading || (!!season && !!week && matchupQuery.isLoading);

  if (configQuery.isError) {
    return (
      <Page>
        <SpotterHeader />
        <Wrap>
          <SelectPortalHost ref={setSelectPortal} />
          <Banner error={SPOTTER_STRINGS.gamebook.loadError} padTop />
        </Wrap>
      </Page>
    );
  }

  if (isLoading || season === undefined || week === undefined) {
    return (
      <Page>
        <SpotterHeader />
        <Spinner padTop />
      </Page>
    );
  }

  return (
    <Page>
      <SpotterHeader />
      <Wrap>
        <SelectPortalHost ref={setSelectPortal} />

        <SeasonWeekPicker
          season={season}
          week={week}
          currentSeason={config?.currentSeason ?? season}
          onSeasonChange={setSeason}
          onWeekChange={setWeek}
          clubCode={config?.clubCode}
          clubName={config?.clubName}
          weekStatus={weeksQuery.data}
          portalTarget={selectPortal}
        />

        {matchupQuery.isError && (
          <Banner error={SPOTTER_STRINGS.gamebook.loadError} padBottom />
        )}

        {matchup?.status === "not-configured" && (
          <StatusMessage>
            {SPOTTER_STRINGS.gamebook.notConfigured}
          </StatusMessage>
        )}

        {matchup?.status === "scheduled" && (
          <MatchupCard>
            <GenRow>
              <GenInfo>
                <h3>
                  {SPOTTER_STRINGS.gamebook.generateTitle(weekLabel(week))}
                </h3>
                <p>{SPOTTER_STRINGS.gamebook.generateBody}</p>
                <OppLine>
                  {matchup.preview ? (
                    SPOTTER_STRINGS.gamebook.opponentLine(
                      matchup.preview.home,
                      matchup.preview.opponentName
                    )
                  ) : (
                    <span className="pend">
                      {SPOTTER_STRINGS.gamebook.opponentPending}
                    </span>
                  )}
                </OppLine>
              </GenInfo>
              <Button
                icon={RefreshCw}
                label={
                  generateMutation.isLoading
                    ? SPOTTER_STRINGS.gamebook.generating
                    : SPOTTER_STRINGS.gamebook.generateButton
                }
                type="default"
                loading={generateMutation.isLoading}
                disabled={isGenerationInFlight}
                tooltip={
                  isGenerationInFlight
                    ? SPOTTER_STRINGS.gamebook.refreshDisabledTooltip
                    : undefined
                }
                onClick={() => {
                  // Capture the week being generated AT CLICK TIME (not whatever `season`/
                  // `week` happen to be once the request resolves) — see `pendingGeneration`'s
                  // doc comment above for why that distinction matters.
                  if (season === undefined || week === undefined) {
                    return;
                  }
                  const target = { season, week };
                  generateMutation.mutate(undefined, {
                    onSuccess: () => {
                      setPendingGeneration({
                        ...target,
                        startedAt: Date.now(),
                      });
                      setPollTimedOut(false);
                    },
                  });
                }}
              />
            </GenRow>
            {generateMutation.isError && (
              <Banner
                error={mutationErrorMessage(
                  generateMutation.error,
                  SPOTTER_STRINGS.gamebook.generateError
                )}
                padTop
              />
            )}
            {isPollingTarget && (
              <StatusMessage style={{ marginTop: 12 }}>
                {pollTimedOut ? (
                  SPOTTER_STRINGS.gamebook.generateTimeout
                ) : (
                  <StatusMessageRow>
                    <InlineSpinner small />
                    <span>{SPOTTER_STRINGS.gamebook.generateSuccess}</span>
                  </StatusMessageRow>
                )}
              </StatusMessage>
            )}
          </MatchupCard>
        )}

        {matchup?.status === "completed" && game && (
          <>
            <MatchupCard>
              <MatchupHead>
                {(() => {
                  const home = game.home === config?.clubCode;
                  const my = home
                    ? game.plays[game.plays.length - 1]?.homeScore ?? 0
                    : game.plays[game.plays.length - 1]?.awayScore ?? 0;
                  const their = home
                    ? game.plays[game.plays.length - 1]?.awayScore ?? 0
                    : game.plays[game.plays.length - 1]?.homeScore ?? 0;
                  const result = my > their ? "w" : my < their ? "l" : "t";
                  const opponent = home ? game.away : game.home;
                  const resultLabel =
                    result === "w" ? "WIN" : result === "l" ? "LOSS" : "TIE";
                  // Same lead/trail tone as the table's own scoreTone (my/their here instead of
                  // away/home — the function itself doesn't care which side is which).
                  const finalTone = scoreTone(my, their);
                  return (
                    <>
                      {/* Spelled out (like the row's own SCORE/FLAG/KEY badges) instead of a
                              single letter + tooltip — Addison: no tooltip actually landed on this
                              badge, and spelling it out makes one unnecessary anyway. */}
                      <WlBadge $result={result}>{resultLabel}</WlBadge>
                      <ScoreRow>
                        <TeamScore $me>
                          <span className="code">{config?.clubCode}</span>
                          <span className={`score ${finalTone.away}`}>
                            {my}
                          </span>
                        </TeamScore>
                        <span>–</span>
                        <TeamScore>
                          <span className={`score ${finalTone.home}`}>
                            {their}
                          </span>
                          <span className="code">{opponent}</span>
                        </TeamScore>
                      </ScoreRow>
                      <HomeAway>
                        {SPOTTER_STRINGS.gamebook.opponentLine(home, opponent)}
                        {game.location ? ` · ${game.location}` : ""}
                      </HomeAway>
                    </>
                  );
                })()}
                {/* Addison, round 25: the week/season/location-only summary and play count got
                        dropped — the season/week pickers above already say which week this is. The
                        game id (for copying) is floated to the far right of this row. */}
                <GameIdSlot>
                  <CopyButton
                    value={game.gameId}
                    label={game.gameId}
                    title="Copy game identifier"
                  />
                  <Button
                    icon={RefreshCw}
                    label={
                      generateMutation.isLoading
                        ? SPOTTER_STRINGS.gamebook.refreshing
                        : SPOTTER_STRINGS.gamebook.refreshButton
                    }
                    type="default"
                    loading={generateMutation.isLoading}
                    disabled={isGenerationInFlight}
                    tooltip={
                      isGenerationInFlight
                        ? SPOTTER_STRINGS.gamebook.refreshDisabledTooltip
                        : undefined
                    }
                    onClick={() => {
                      // Same click-time-capture reasoning as the scheduled card's Generate button
                      // (see `pendingGeneration`'s doc comment) — `baselineGeneratedAt` additionally
                      // records the CURRENT `lastGeneratedAt` so the completion effect above can tell
                      // this specific refresh apart from the game's prior (already-completed) state.
                      if (season === undefined || week === undefined) {
                        return;
                      }
                      const target = { season, week };
                      generateMutation.mutate(undefined, {
                        onSuccess: () => {
                          setPendingGeneration({
                            ...target,
                            startedAt: Date.now(),
                            baselineGeneratedAt: game.lastGeneratedAt ?? null,
                          });
                          setPollTimedOut(false);
                        },
                      });
                    }}
                  />
                </GameIdSlot>
              </MatchupHead>

              {generateMutation.isError && (
                <Banner
                  error={mutationErrorMessage(
                    generateMutation.error,
                    SPOTTER_STRINGS.gamebook.refreshError
                  )}
                  padTop
                />
              )}
              {isPollingTarget &&
                pendingGeneration?.baselineGeneratedAt !== undefined && (
                  <StatusMessage style={{ marginTop: 12 }}>
                    {pollTimedOut ? (
                      SPOTTER_STRINGS.gamebook.refreshTimeout
                    ) : (
                      <StatusMessageRow>
                        <InlineSpinner small />
                        <span>{SPOTTER_STRINGS.gamebook.refreshSuccess}</span>
                      </StatusMessageRow>
                    )}
                  </StatusMessage>
                )}
            </MatchupCard>

            <Toolbar>
              <QuickChips>
                <QuickChip
                  $on={quarterFilter === "all"}
                  onClick={() => setQuarterFilter("all")}
                >
                  {SPOTTER_STRINGS.gamebook.toolbar.allQuarters}
                </QuickChip>
                {quarters.map((qn) => (
                  <QuickChip
                    key={qn}
                    $on={quarterFilter === String(qn)}
                    onClick={() => setQuarterFilter(String(qn))}
                  >
                    {qn >= 5
                      ? SPOTTER_STRINGS.gamebook.toolbar.overtime
                      : SPOTTER_STRINGS.gamebook.toolbar.quarter(qn)}
                  </QuickChip>
                ))}
              </QuickChips>
              <SearchWrap>
                <Input
                  icon={Search}
                  value={search}
                  onChange={setSearch}
                  placeholder={
                    SPOTTER_STRINGS.gamebook.toolbar.searchPlaceholder
                  }
                  clearable
                  noPadding
                />
              </SearchWrap>
              <Toggle
                $on={scoringOnly}
                $tone="sc"
                onClick={() => setScoringOnly((v) => !v)}
              >
                <ToggleDot $on={scoringOnly} $tone="sc" />
                {SPOTTER_STRINGS.gamebook.toolbar.scoringToggle} ·{" "}
                {scoringCount}
              </Toggle>
              <Toggle
                $on={penaltiesOnly}
                $tone="pn"
                onClick={() => setPenaltiesOnly((v) => !v)}
              >
                <ToggleDot $on={penaltiesOnly} $tone="pn" />
                {SPOTTER_STRINGS.gamebook.toolbar.penaltiesToggle} ·{" "}
                {penaltyCount}
              </Toggle>
              <Toggle
                $on={criticalOnly}
                $tone="cr"
                onClick={() => setCriticalOnly((v) => !v)}
              >
                <ToggleDot $on={criticalOnly} $tone="cr" />
                {SPOTTER_STRINGS.gamebook.toolbar.criticalToggle} ·{" "}
                {criticalCount}
              </Toggle>
              <Count>
                <b>{filtered.length}</b>{" "}
                {SPOTTER_STRINGS.gamebook.toolbar.shownWord} ·{" "}
                {game.plays.length} {SPOTTER_STRINGS.gamebook.toolbar.playsWord}
              </Count>
              <DownloadButton
                onClick={() => {
                  const suffix =
                    (quarterFilter !== "all" ? `_Q${quarterFilter}` : "") +
                    (scoringOnly ? "_scoring" : "") +
                    (penaltiesOnly ? "_penalties" : "") +
                    (criticalOnly ? "_critical" : "") +
                    (search ? "_search" : "");
                  downloadGamebookCsv(
                    game,
                    filtered.map(({ play }) => play),
                    suffix
                  );
                }}
              >
                <Download />
                {SPOTTER_STRINGS.gamebook.toolbar.downloadCsv(filtered.length)}
              </DownloadButton>
            </Toolbar>

            {isMobile ? (
              <MobilePlayList>{playCards}</MobilePlayList>
            ) : (
              <>
                <ColheadSticky>
                  <Colhead>
                    <div>{SPOTTER_STRINGS.gamebook.columns.uid}</div>
                    <div>{SPOTTER_STRINGS.gamebook.columns.situation}</div>
                    <div>{SPOTTER_STRINGS.gamebook.columns.possession}</div>
                    <div>{SPOTTER_STRINGS.gamebook.columns.play}</div>
                    <div>{SPOTTER_STRINGS.gamebook.columns.score}</div>
                    <div>{SPOTTER_STRINGS.gamebook.columns.flags}</div>
                  </Colhead>
                </ColheadSticky>
                <TableCard noMargin>
                  <Table
                    columns={COLUMN_WIDTHS.map((width) => ({ width }))}
                    columnGrid
                    rows={rows}
                    rowHeight={52}
                    rowPadding={`5px 10px 5px ${ROW_LEFT_PADDING}px`}
                  />
                </TableCard>
              </>
            )}
          </>
        )}
      </Wrap>
    </Page>
  );
};

export default GamebookScreen;
